/**
 * GWP Float Widget Component
 * Manages gift with purchase floating widget with cart monitoring
 */

class GwpFloatWidget extends HTMLElement {
  constructor() {
    super();

    // Configuration
    this.cartThreshold = parseInt(this.dataset.cartThreshold) || 9999;
    this.gwpMessage =
      this.dataset.gwpMessage || "Free gift with your purchase!";
    this.promoTitle = this.dataset.promoTitle || "Gift With Purchase";
    this.showOnlyWithDiscountCode =
      this.dataset.showOnlyWithDiscountCode === "true";

    // State
    this.isExpanded = false;
    this.isQualified = false;
    this.wasQualified = false; // Track previous qualification state
    this.gwpInCart = false;
    this.currentCartTotal = 0;
    this.hasDiscountCode = false;

    // Auto-open tracking
    this.autoOpenKey = 'gwp-float-auto-open-count';
    this.maxAutoOpens = 3;

    // Shake interval tracking
    this.shakeInterval = null;
    this.shakeIntervalDelay = 3000; // 3 seconds

    // DOM References
    this.refs = {
      toggle: this.querySelector(".gwp-float__toggle"),
      panel: this.querySelector(".gwp-float__panel"),
      closeBtn: this.querySelector(".gwp-float__close"),
      progressFill: this.querySelector("[data-progress-fill]"),
      progressText: this.querySelector("[data-progress-text]"),
      addBtn: this.querySelector("[data-add-gwp]"),
      products: this.querySelectorAll(".gwp-float__product"),
    };

    // Bind methods
    this.handleToggle = this.handleToggle.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    if (this.cartPollInterval) {
      clearInterval(this.cartPollInterval);
    }
    if (this.shakeInterval) {
      clearInterval(this.shakeInterval);
    }
  }

  /**
   * Initialize component
   */
  async init() {
    this.setupEventListeners();

    // Show widget immediately, will update based on cart
    this.dataset.hidden = "false";

    // Fetch cart and check for qualification on initial load.
    // checkCart() (called inside fetchCart) handles shake + auto-open.
    await this.fetchCart();
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Toggle expand/collapse
    this.refs.toggle?.addEventListener("click", this.handleToggle);
    this.refs.closeBtn?.addEventListener("click", this.handleClose);

    // Add to cart
    this.refs.addBtn?.addEventListener("click", this.handleAddToCart);

    // Sync product element's data-variant-id when user changes the size select
    this.querySelectorAll("[data-variant-select]").forEach((select) => {
      select.addEventListener("change", (e) => {
        const productId = e.target.dataset.variantSelect;
        const product = this.querySelector(
          `.gwp-float__product[data-product-id="${productId}"]`
        );
        if (product) product.dataset.variantId = e.target.value;
      });
    });

    // Cart updates - listen to multiple event types
    document.addEventListener("cart:updated", this.handleCartUpdate);
    document.addEventListener("cart:update", this.handleCartUpdate);
    document.addEventListener("cart:build", this.handleCartUpdate);
    document.addEventListener("ajaxProduct:added", () => this.fetchCart());
    document.addEventListener("cart:change", () => this.fetchCart());
    document.addEventListener("cart:refresh", () => this.fetchCart());
    document.addEventListener("product:added-to-cart", () => this.fetchCart());

    // Listen for theme-specific cart drawer events
    document.addEventListener("theme:cart:change", () => this.fetchCart());
    document.addEventListener("cart-drawer:open", () => this.fetchCart());
    document.addEventListener("cart-drawer:close", () => this.fetchCart());

    // Listen for discount-related events
    document.addEventListener("discount:applied", () => this.fetchCart());
    document.addEventListener("discount:removed", () => this.fetchCart());
    document.addEventListener("cart:discount-applied", () => this.fetchCart());
    document.addEventListener("cart:discount-removed", () => this.fetchCart());

    // Click outside to close
    document.addEventListener("click", this.handleClickOutside);

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isExpanded) {
        this.handleClose();
      }
    });

    // Poll cart periodically as fallback (every 15 seconds)
    this.cartPollInterval = setInterval(() => {
      this.fetchCart();
    }, 15000);
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    this.refs.toggle?.removeEventListener("click", this.handleToggle);
    this.refs.closeBtn?.removeEventListener("click", this.handleClose);
    this.refs.addBtn?.removeEventListener("click", this.handleAddToCart);
    document.removeEventListener("cart:updated", this.handleCartUpdate);
    document.removeEventListener("cart:update", this.handleCartUpdate);
    document.removeEventListener("cart:build", this.handleCartUpdate);
    document.removeEventListener("click", this.handleClickOutside);
  }

  /**
   * Handle toggle button click
   */
  handleToggle(e) {
    e.stopPropagation();
    this.isExpanded = !this.isExpanded;
    this.updatePanelState();
  }

  /**
   * Handle close button click
   */
  handleClose(e) {
    if (e) e.stopPropagation();
    this.isExpanded = false;
    this.updatePanelState();
  }

  /**
   * Handle click outside to close panel
   */
  handleClickOutside(e) {
    if (this.isExpanded && !this.contains(e.target)) {
      this.handleClose();
    }
  }

  /**
   * Update panel expanded/collapsed state
   */
  updatePanelState() {
    const { toggle, panel } = this.refs;

    if (this.isExpanded) {
      toggle?.setAttribute("aria-expanded", "true");
      panel?.setAttribute("aria-hidden", "false");
      this.focusTrap();
    } else {
      toggle?.setAttribute("aria-expanded", "false");
      panel?.setAttribute("aria-hidden", "true");
      // Return focus to the toggle button for accessibility
      toggle?.focus();
    }
  }

  /**
   * Get auto-open count from localStorage
   * @returns {number}
   */
  getAutoOpenCount() {
    try {
      const count = localStorage.getItem(this.autoOpenKey);
      return count ? parseInt(count, 10) : 0;
    } catch (error) {
      console.error('[GWP Float Widget] Error reading localStorage:', error);
      return 0;
    }
  }

  /**
   * Increment auto-open count in localStorage
   */
  incrementAutoOpenCount() {
    try {
      const currentCount = this.getAutoOpenCount();
      localStorage.setItem(this.autoOpenKey, String(currentCount + 1));
    } catch (error) {
      console.error('[GWP Float Widget] Error writing to localStorage:', error);
    }
  }

  /**
   * Trigger shake animation on the icon
   */
  triggerShake() {
    const iconElement = this.querySelector('.gwp-float__toggle-icon');
    if (!iconElement) return;

    // Add shake class
    iconElement.classList.add('gwp-float__toggle-icon--shake');

    // Remove class after animation completes
    setTimeout(() => {
      iconElement.classList.remove('gwp-float__toggle-icon--shake');
    }, 600);
  }

  /**
   * Start repeating shake animation every 3 seconds
   */
  startShakeInterval() {
    // Clear any existing interval first
    this.stopShakeInterval();

    // Trigger shake immediately
    this.triggerShake();

    // Set up interval to repeat shake every 3 seconds
    this.shakeInterval = setInterval(() => {
      this.triggerShake();
    }, this.shakeIntervalDelay);
  }

  /**
   * Stop repeating shake animation
   */
  stopShakeInterval() {
    if (this.shakeInterval) {
      clearInterval(this.shakeInterval);
      this.shakeInterval = null;
    }
  }

  /**
   * Auto-open panel if under max opens limit
   */
  autoOpenPanel() {
    const openCount = this.getAutoOpenCount();

    if (openCount < this.maxAutoOpens && !this.isExpanded) {
      // Delay opening slightly to let shake animation complete first
      setTimeout(() => {
        this.isExpanded = true;
        this.updatePanelState();
        this.incrementAutoOpenCount();
      }, 700);
    }
  }

  /**
   * Simple focus trap for accessibility
   */
  focusTrap() {
    const focusableElements = this.refs.panel?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements?.length) {
      focusableElements[0]?.focus();
    }
  }

  /**
   * Fetch current cart data
   */
  async fetchCart() {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const cart = await response.json();
        this.checkCart(cart);
      }
    } catch (error) {
      console.error("[GWP Float Widget] Error fetching cart:", error);
    }
  }

  /**
   * Handle cart update event
   */
  handleCartUpdate(event) {
    if (event.detail?.cart) {
      this.checkCart(event.detail.cart);
    }
  }

  /**
   * Check cart and update widget state
   * @param {Object} cart - Shopify cart object
   */
  checkCart(cart) {
    this.currentCartTotal = cart.total_price || 0;

    // Check if discount codes are present
    this.hasDiscountCode =
      (cart.discount_codes && cart.discount_codes.length > 0) ||
      (cart.discount_applications && cart.discount_applications.length > 0);

    // Check if GWP products are already in cart
    this.gwpInCart = this.checkGwpInCart(cart.items || []);

    // Store previous qualification state
    const previouslyQualified = this.wasQualified;

    // Check if qualified
    this.isQualified = this.currentCartTotal >= this.cartThreshold;

    // Detect qualification state change (became qualified)
    if (!previouslyQualified && this.isQualified && !this.gwpInCart) {
      // User just became qualified
      this.startShakeInterval();
      this.autoOpenPanel();

      // Add qualified class to toggle button for pulsing effect
      this.refs.toggle?.classList.add('gwp-float__toggle--qualified');
    } else if (previouslyQualified && !this.isQualified) {
      // User is no longer qualified
      this.stopShakeInterval();
      this.refs.toggle?.classList.remove('gwp-float__toggle--qualified');
    } else if (this.isQualified && !this.gwpInCart) {
      // Still qualified - keep the pulsing effect and shake
      this.refs.toggle?.classList.add('gwp-float__toggle--qualified');

      // Ensure shake interval is running
      if (!this.shakeInterval) {
        this.startShakeInterval();
      }
    } else {
      // Not qualified or GWP already in cart
      this.stopShakeInterval();
      this.refs.toggle?.classList.remove('gwp-float__toggle--qualified');
    }

    // Update wasQualified for next comparison
    this.wasQualified = this.isQualified;

    // Update UI
    this.updateProgress();
    this.updateVisibility();
    this.updateAddButton();
  }

  /**
   * Check if GWP products are in cart by product ID,
   * so any variant of the gift is detected regardless of the current select value.
   * @param {Array} cartItems - Cart items array
   * @returns {boolean}
   */
  checkGwpInCart(cartItems) {
    const gwpProductIds = Array.from(this.refs.products)
      .map((p) => parseInt(p.dataset.productId, 10))
      .filter(Boolean);
    return cartItems.some((item) => gwpProductIds.includes(item.product_id));
  }

  /**
   * Get the currently-selected GWP variant IDs (used for add-to-cart).
   * @returns {Array<number>}
   */
  getGwpVariantIds() {
    const products = Array.from(this.refs.products);
    return products
      .map((product) => {
        const productId = product.dataset.productId;
        const select = this.querySelector(
          `[data-variant-select="${productId}"]`
        );
        const id = select ? select.value : product.dataset.variantId;
        return parseInt(id, 10);
      })
      .filter(Boolean);
  }

  /**
   * Update progress bar and text
   */
  updateProgress() {
    const { progressFill, progressText } = this.refs;

    if (!progressFill || !progressText) return;

    // Calculate progress percentage
    const percentage = Math.min(
      (this.currentCartTotal / this.cartThreshold) * 100,
      100
    );
    progressFill.style.width = `${percentage}%`;

    // Update progress text
    if (this.isQualified) {
      progressText.replaceChildren(
        this.gwpInCart
          ? "Free gift added to your cart!"
          : "Click below to add your free gift!"
      );
      progressText.classList.add("gwp-float__progress-text--qualified");
    } else {
      const remaining = this.cartThreshold - this.currentCartTotal;
      const formattedAmount = this.formatMoney(remaining);
      const note = document.createElement("span");
      note.className = "gwp-float__progress-note";
      note.textContent = "After discount";
      progressText.replaceChildren(
        `Spend ${formattedAmount} more and get a FREE gift`,
        note
      );
      progressText.classList.remove("gwp-float__progress-text--qualified");
    }
  }

  /**
   * Update widget visibility based on qualification and discount codes
   */
  updateVisibility() {
    // Check discount code requirement first
    if (this.showOnlyWithDiscountCode && !this.hasDiscountCode) {
      this.dataset.hidden = "true";
      this.handleClose(); // Close panel if discount code is required but not present
      return;
    }

    // Show widget unless GWP is already in cart
    if (this.gwpInCart) {
      this.dataset.hidden = "true";
      this.handleClose();
    } else {
      this.dataset.hidden = "false";
    }
  }

  /**
   * Update add button state
   */
  updateAddButton() {
    const { addBtn } = this.refs;
    if (!addBtn) return;

    if (this.isQualified && !this.gwpInCart) {
      addBtn.disabled = false;
    } else {
      addBtn.disabled = true;
    }
  }

  /**
   * Handle add to cart button click
   */
  async handleAddToCart() {
    if (!this.isQualified || this.gwpInCart) return;

    const variantIds = this.getGwpVariantIds();
    if (variantIds.length === 0) return;

    try {
      // Disable button during request
      this.refs.addBtn.disabled = true;

      // Prepare items to add (all GWP products)
      const items = variantIds.map((id) => ({
        id: id,
        quantity: 1,
        properties: {
          _gwp: "true",
          _promo: this.promoTitle,
        },
      }));

      // Add to cart
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });

      if (response.ok) {
        const data = await response.json();

        // Dispatch custom event
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added", {
            detail: { product: data },
          })
        );

        // Fetch updated cart (this will hide the widget when GWP is detected)
        await this.fetchCart();
      } else {
        const error = await response.json();
        console.error("[GWP Float Widget] Error adding to cart:", error);
        this.refs.addBtn.disabled = false;
      }
    } catch (error) {
      console.error("[GWP Float Widget] Error:", error);
      this.refs.addBtn.disabled = false;
    }
  }

  /**
   * Format money with currency
   * @param {number} cents - Amount in cents
   * @returns {string}
   */
  formatMoney(cents) {
    const dollars = cents / 100;
    const formatted = dollars.toFixed(2);

    // Use Shopify money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(
        cents,
        window.theme?.moneyFormat || "${{amount}}"
      );
    }

    // Use theme currency format if available
    if (window.theme && window.theme.moneyFormat) {
      return window.theme.moneyFormat.replace("{{amount}}", formatted);
    }

    // Fallback to basic format
    return `$${formatted}`;
  }
}

// Log script loading
console.log("[GWP Float Widget] Script loaded");

// Register custom element
if (!customElements.get("gwp-float-widget")) {
  customElements.define("gwp-float-widget", GwpFloatWidget);
  console.log("[GWP Float Widget] Custom element registered");
} else {
  console.log("[GWP Float Widget] Custom element already registered");
}

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", () => {
  const widget = document.querySelector("gwp-float-widget");
  if (!widget) {
    console.warn(
      "[GWP Float Widget] Widget NOT found in DOM - check if promo is configured and active"
    );
  }
});